/**
 * PSD source-stash — per-tab IndexedDB store of original PSD bytes plus
 * the editor-to-PSD layer-index mapping. This is the foundation for
 * **lossless round-trip**: when a user opens a PSD with features we
 * don't natively model (smart objects, layer effects, advanced text,
 * adjustment kinds we don't yet preserve), we flatten those layers to
 * raster for display but keep the original bytes here. On export
 * (next phase) the unmodified layers are restored from this stash so
 * the user doesn't lose data they didn't directly touch.
 *
 * Bytes are gzip-compressed on write (CompressionStream — native;
 * ~3× typical on PSD files), decompressed on read. Quota is checked
 * via `navigator.storage.estimate()` BEFORE the write so partially-
 * imported PSDs aren't left in a half-stashed state.
 *
 * The stash entry is keyed by `documentId` — the same UUID that keys
 * the session record in `sessions`. `session-store.clearSession()`
 * deletes both atomically (see its transaction over both stores),
 * so the only GC this module owns is the optional best-effort
 * startup-time orphan sweep (`gcOrphanedStashes`).
 *
 * No PSD parsing here — that's `lib/psd-import.ts`. This module only
 * shuttles bytes + metadata between editor memory and IDB.
 */

import { openImageEditorDb, SESSION_STORE_NAMES } from './session-store';

/** Maps editor LayerNode.id → which PSD layer it originated from.
 *  `psdLayerIndex` is the depth-first ordinal in the original PSD's
 *  tree, computed once at import time and stable across edits in
 *  the editor (the editor's tree shape is authoritative on export;
 *  the index simply locates the source data in the re-parsed PSD).
 *
 *  Layers created in the editor (paste, new layer, Smart Create, etc.)
 *  have no entry here — those export as raster, not as a restoration
 *  of any PSD layer. */
export interface PsdLayerOrigin {
  psdLayerIndex: number;
}

/** Persisted shape of a stash entry. `psdBytesGz` is the gzip-
 *  compressed original `.psd` (or `.psb`) file. */
export interface StoredPsdStash {
  documentId: string;
  /** Gzip-compressed bytes. Compress ratio is roughly 1.5–3× on
   *  typical PSDs (RLE is already applied by Photoshop to layer
   *  pixel chunks; metadata + chunks not RLE-compressed gain more). */
  psdBytesGz: ArrayBuffer;
  /** Uncompressed size — used for the quota pre-check and surfaced
   *  to the user in error messages. */
  uncompressedSize: number;
  layerOriginById: Record<string, PsdLayerOrigin>;
  originalWidth: number;
  originalHeight: number;
  savedAt: number;
}

/** In-memory shape returned to callers — `psdBytes` is decompressed,
 *  `compressedSize` is informational. */
export interface PsdStash {
  documentId: string;
  psdBytes: ArrayBuffer;
  uncompressedSize: number;
  compressedSize: number;
  layerOriginById: Record<string, PsdLayerOrigin>;
  originalWidth: number;
  originalHeight: number;
  savedAt: number;
}

/** Thrown when the storage budget can't accommodate the requested
 *  PSD import. Caller surfaces a clear toast and refuses the import
 *  — we'd rather refuse than leave a partial stash behind. */
export class PsdStashQuotaError extends Error {
  constructor(
    public readonly requiredBytes: number,
    public readonly availableBytes: number,
  ) {
    super(
      `PSD stash needs ~${formatMb(requiredBytes)} MB but only ${formatMb(availableBytes)} MB is available`,
    );
    this.name = 'PsdStashQuotaError';
  }
}

function formatMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1);
}

/** Conservative ratio: compressed size + room for the layer-origin
 *  map + book-keeping. Real ratios are ~30-40% of uncompressed; we
 *  reserve 50% to allow for pathological cases (e.g. PSDs whose
 *  content is mostly noise and doesn't compress). */
const QUOTA_OVERHEAD_RATIO = 0.5;

async function gzipCompress(input: ArrayBuffer): Promise<ArrayBuffer> {
  const stream = new Blob([input]).stream().pipeThrough(new CompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}

async function gzipDecompress(input: ArrayBuffer): Promise<ArrayBuffer> {
  const stream = new Blob([input]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}

/** Returns the remaining IndexedDB quota in bytes, or `Infinity` if
 *  the browser doesn't surface storage estimates (older Safari, some
 *  embedded engines). When `Infinity`, the caller skips the pre-check
 *  and relies on the `QuotaExceededError` fallback in `put()`. */
async function availableQuotaBytes(): Promise<number> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return Number.POSITIVE_INFINITY;
  }
  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (typeof quota !== 'number' || typeof usage !== 'number') {
      return Number.POSITIVE_INFINITY;
    }
    return Math.max(0, quota - usage);
  } catch {
    // Quota estimation failed (e.g. private-window restrictions);
    // fall back to optimistic — the caller still gets a clear error
    // if the actual put() fails.
    return Number.POSITIVE_INFINITY;
  }
}

/** Persist a PSD stash entry. Compresses the bytes, runs a quota
 *  pre-check, and writes the entry to the `psd-stashes` store keyed
 *  by `documentId`. Throws {@link PsdStashQuotaError} on insufficient
 *  budget. On other IDB failures (very rare), the error is logged and
 *  re-thrown so the caller can surface it. */
export async function savePsdStash(stash: {
  documentId: string;
  psdBytes: ArrayBuffer;
  layerOriginById: Record<string, PsdLayerOrigin>;
  originalWidth: number;
  originalHeight: number;
}): Promise<void> {
  const required = stash.psdBytes.byteLength * (1 + QUOTA_OVERHEAD_RATIO);
  const available = await availableQuotaBytes();
  if (available < required && Number.isFinite(available)) {
    throw new PsdStashQuotaError(required, available);
  }

  const psdBytesGz = await gzipCompress(stash.psdBytes);
  const record: StoredPsdStash = {
    documentId: stash.documentId,
    psdBytesGz,
    uncompressedSize: stash.psdBytes.byteLength,
    layerOriginById: stash.layerOriginById,
    originalWidth: stash.originalWidth,
    originalHeight: stash.originalHeight,
    savedAt: Date.now(),
  };

  const db = await openImageEditorDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SESSION_STORE_NAMES.psdStashes, 'readwrite');
    const store = tx.objectStore(SESSION_STORE_NAMES.psdStashes);
    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => {
      // QuotaExceededError surfaces here if the pre-check missed
      // (e.g. concurrent writes between estimate and put). Re-throw
      // so callers can decide whether to retry / refuse.
      reject(request.error);
    };
  });
}

/** Load a stash entry by documentId. Returns null if absent. Decompresses
 *  bytes on read so callers see a usable ArrayBuffer. */
export async function loadPsdStash(documentId: string): Promise<PsdStash | null> {
  const db = await openImageEditorDb();
  const record = await new Promise<StoredPsdStash | null>((resolve) => {
    const tx = db.transaction(SESSION_STORE_NAMES.psdStashes, 'readonly');
    const store = tx.objectStore(SESSION_STORE_NAMES.psdStashes);
    const request = store.get(documentId);
    request.onsuccess = () => resolve((request.result as StoredPsdStash | undefined) ?? null);
    request.onerror = () => {
      console.warn('[image-editor] loadPsdStash failed:', request.error);
      resolve(null);
    };
  });
  if (!record) return null;
  const psdBytes = await gzipDecompress(record.psdBytesGz);
  return {
    documentId: record.documentId,
    psdBytes,
    uncompressedSize: record.uncompressedSize,
    compressedSize: record.psdBytesGz.byteLength,
    layerOriginById: record.layerOriginById,
    originalWidth: record.originalWidth,
    originalHeight: record.originalHeight,
    savedAt: record.savedAt,
  };
}

/** Returns true if a stash entry exists for the given documentId.
 *  Cheap check that avoids the gzip-decompression round trip in
 *  `loadPsdStash` when the caller only needs presence. */
export async function hasPsdStash(documentId: string): Promise<boolean> {
  const db = await openImageEditorDb();
  return new Promise<boolean>((resolve) => {
    const tx = db.transaction(SESSION_STORE_NAMES.psdStashes, 'readonly');
    const store = tx.objectStore(SESSION_STORE_NAMES.psdStashes);
    const request = store.count(documentId);
    request.onsuccess = () => resolve(request.result > 0);
    request.onerror = () => resolve(false);
  });
}

/** Delete stash entries whose documentId doesn't match any active
 *  session record. Best-effort cleanup intended to run once on
 *  editor startup — a crash or interrupted close could otherwise
 *  leave orphan stashes in IDB until the user manually clears
 *  browsing data. Tab-close + last-tab-close paths run their own
 *  cleanups (see `clearSession` / `clearAllSessions`); this exists
 *  for the unhappy path. */
export async function gcOrphanedStashes(activeDocumentIds: Iterable<string>): Promise<number> {
  const live = new Set(activeDocumentIds);
  const db = await openImageEditorDb();
  return new Promise<number>((resolve) => {
    let removed = 0;
    const tx = db.transaction(SESSION_STORE_NAMES.psdStashes, 'readwrite');
    const store = tx.objectStore(SESSION_STORE_NAMES.psdStashes);
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      const record = cursor.value as StoredPsdStash;
      if (!live.has(record.documentId)) {
        cursor.delete();
        removed += 1;
      }
      cursor.continue();
    };
    tx.oncomplete = () => resolve(removed);
    tx.onerror = () => {
      console.warn('[image-editor] gcOrphanedStashes failed:', tx.error);
      resolve(removed);
    };
  });
}
